import React, { useState, useEffect, useCallback } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShopSyncAPI from '../../services/api/integrations/shop-sync-api';
import ShopExceptionsDialog from '../admin/projects/ShopExceptionsDialog';
import Logger from '../../utils/logger';

/**
 * Warning chip for unresolved shop time sync exceptions on the selected
 * project. Click opens ShopExceptionsDialog (view-only unless canResolve).
 * Renders nothing when count is 0 or project/token missing.
 */
const ShopSyncExceptionChip = ({
  projectId,
  projectName = '',
  token,
  canResolve = false,
}) => {
  const [count, setCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadCount = useCallback(async () => {
    if (!projectId || !token) {
      setCount(0);
      return;
    }
    try {
      const counts = await ShopSyncAPI.getExceptionCounts(token);
      const match = counts.find(
        (c) => String(c.fieldtime_project_id) === String(projectId)
      );
      setCount(match?.count || 0);
    } catch (err) {
      Logger.error('Error loading shop exception count', err);
      setCount(0);
    }
  }, [projectId, token]);

  useEffect(() => {
    loadCount();
  }, [loadCount]);

  if (!projectId || count === 0) return null;

  return (
    <>
      <Box sx={{ display: 'inline-flex' }}>
        <Tooltip title="Unresolved shop sync exceptions — click to view">
          <Chip
            size="small"
            icon={
              <WarningAmberIcon
                fontSize="small"
                sx={{ color: '#92400e !important', fontSize: '14px !important' }}
              />
            }
            label={`${count} exception${count === 1 ? '' : 's'}`}
            onClick={() => setDialogOpen(true)}
            sx={{
              bgcolor: '#fef3c7',
              color: '#92400e',
              border: '1px solid #fde68a',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
              height: 26,
              '&:hover': { bgcolor: '#fde68a' },
            }}
          />
        </Tooltip>
      </Box>

      <ShopExceptionsDialog
        open={dialogOpen}
        projectId={projectId}
        projectName={projectName}
        token={token}
        canResolve={canResolve}
        onClose={() => setDialogOpen(false)}
        onResolved={loadCount}
      />
    </>
  );
};

export default ShopSyncExceptionChip;
